/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Complete 10-step wizard for live streaming script generation
 * 
 * Steps:
 * 1. Platform & Host Profile
 * 2. Objective
 * 3. Target Audience + Panggilan + Nada
 * 4. Knowledge Selection
 * 5. Spider Web
 * 6. Evidence Mode
 * 7. Review & Prime
 * 8. SOA Drill (Live-specific)
 * 9. Script Generation
 * 10. Review & Submit
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../../api/liveStudioV2';

// Step Components
import Step1PlatformHost from './steps/Step1PlatformHost';
import Step2Objective from './steps/Step2Objective';
import Step3Audience from './steps/Step3Audience';
import Step4Knowledge from './steps/Step4Knowledge';
import Step5SpiderWeb from './steps/Step5SpiderWeb';
import Step6Evidence from './steps/Step6Evidence';
import Step7ReviewPrime from './steps/Step7ReviewPrime';
import Step8SOADrill from './steps/Step8SOADrill';
import Step9ScriptGeneration from './steps/Step9ScriptGeneration';
import Step10ReviewSubmit from './steps/Step10ReviewSubmit';

// Scroll styles
const scrollStyles = `
  .live-studio-v2-scrollable {
    scroll-behavior: smooth;
  }
  
  .live-studio-v2-scrollable::-webkit-scrollbar {
    width: 6px;
  }
  
  .live-studio-v2-scrollable::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }
  
  .live-studio-v2-scrollable::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
  
  .live-studio-v2-scrollable::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const LiveStudioV2 = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Session management
  const [sessionId, setSessionId] = useState(() => {
    const sessionFromUrl = searchParams.get('session');
    if (sessionFromUrl && sessionFromUrl.startsWith('LS_')) {
      return sessionFromUrl;
    }
    
    const stored = localStorage.getItem('ls_v2_session_id');
    if (stored) {
      return stored;
    }
    
    const newSession = `LS_${Date.now()}`;
    return newSession;
  });
  
  const [currentStep, setCurrentStep] = useState(() => {
    const stepFromUrl = searchParams.get('step');
    if (stepFromUrl) {
      const stepNum = parseInt(stepFromUrl);
      if (stepNum >= 1 && stepNum <= 10) {
        return stepNum;
      }
    }
    const stored = localStorage.getItem(`ls_v2_${sessionId}_step`);
    return stored ? parseInt(stored) : 1;
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Session data
  const [sessionData, setSessionData] = useState(() => {
    const stored = localStorage.getItem(`ls_v2_${sessionId}_data`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse stored session data:', e);
      }
    }
    return {
      // Step 1: Platform & Host
      platform: null,
      durationMinutes: 60,
      ctaInterval: 5,
      sessionType: 'solo',
      hosts: [],
      
      // Step 2: Objective
      objective: null,
      
      // Step 3: Audience
      audience: null,
      panggilan: null,
      panggilanCustom: null,
      nada: null,
      nadaCustom: null,
      usedAISuggestion: false,
      
      // Step 4: Knowledge
      selectedProducts: [],
      selectedServices: [],
      selectedValues: [],
      selectedAmbassador: null,
      
      // Step 5: Spider Web
      weights: null,
      
      // Step 6: Evidence
      evidenceMode: null,
      
      // Step 7: Review & Prime
      soaPlan: null,
      narrative: null,
      riskNotes: null,
      
      // Step 8: SOA Drill
      soaAnswers: null,
      soaSummary: null,
      
      // Step 9: Script Generation
      scriptConfig: null,
      script: null,
      
      // Step 10: Submit
      submission: null
    };
  });

  // Persist session ID
  useEffect(() => {
    localStorage.setItem('ls_v2_session_id', sessionId);
  }, [sessionId]);

  // Persist session data
  useEffect(() => {
    localStorage.setItem(`ls_v2_${sessionId}_data`, JSON.stringify(sessionData));
  }, [sessionData, sessionId]);

  // Persist current step
  useEffect(() => {
    localStorage.setItem(`ls_v2_${sessionId}_step`, currentStep.toString());
  }, [currentStep, sessionId]);

  // Watch URL parameter changes
  useEffect(() => {
    const stepFromUrl = searchParams.get('step');
    if (stepFromUrl) {
      const stepNum = parseInt(stepFromUrl);
      if (stepNum >= 1 && stepNum <= 10 && stepNum !== currentStep) {
        setCurrentStep(stepNum);
      }
    }
  }, [searchParams, currentStep]);

  // Step definitions
  const steps = [
    { num: 1, title: 'Platform & Host', component: Step1PlatformHost },
    { num: 2, title: 'Objective', component: Step2Objective },
    { num: 3, title: 'Audience', component: Step3Audience },
    { num: 4, title: 'Knowledge', component: Step4Knowledge },
    { num: 5, title: 'Spider Web', component: Step5SpiderWeb },
    { num: 6, title: 'Evidence', component: Step6Evidence },
    { num: 7, title: 'Review & Prime', component: Step7ReviewPrime },
    { num: 8, title: 'SOA Drill', component: Step8SOADrill },
    { num: 9, title: 'Script', component: Step9ScriptGeneration },
    { num: 10, title: 'Submit', component: Step10ReviewSubmit }
  ];

  const handleStepComplete = (stepNum, data) => {
    console.log(`[LiveStudioV2] Step ${stepNum} completed with data:`, data);
    setSessionData(prev => {
      const updated = { ...prev, ...data };
      return updated;
    });
    
    if (stepNum < 10) {
      const nextStep = stepNum + 1;
      setCurrentStep(nextStep);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('step', nextStep.toString());
        return newParams;
      }, { replace: true });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('step', prevStep.toString());
        return newParams;
      }, { replace: true });
    } else {
      // At step 1, go back to dashboard
      navigate('/dashboard');
    }
  };

  const clearSession = () => {
    localStorage.removeItem(`ls_v2_${sessionId}_data`);
    localStorage.removeItem(`ls_v2_${sessionId}_step`);
    localStorage.removeItem('ls_v2_session_id');
    
    setSessionData({
      platform: null,
      durationMinutes: 60,
      ctaInterval: 5,
      sessionType: 'solo',
      hosts: [],
      objective: null,
      audience: null,
      panggilan: null,
      panggilanCustom: null,
      nada: null,
      nadaCustom: null,
      usedAISuggestion: false,
      selectedProducts: [],
      selectedServices: [],
      selectedValues: [],
      selectedAmbassador: null,
      weights: null,
      evidenceMode: null,
      soaPlan: null,
      narrative: null,
      riskNotes: null,
      soaAnswers: null,
      soaSummary: null,
      scriptConfig: null,
      script: null,
      submission: null
    });
    setCurrentStep(1);
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <>
      <style>{scrollStyles}</style>
      
      <div style={{ 
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #0e67a2 0%, #005BBB 100%)'
      }}>
        {/* Header */}
        <div style={{
          background: '#0e67a2',
          padding: '16px 20px',
          color: '#fff',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 100
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                🎥 Live Studio V2
              </h1>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>Session: {sessionId}</p>
            </div>
            <button
              onClick={() => {
                if (window.confirm('Start a new session? Current progress will be lost.')) {
                  clearSession();
                  window.location.reload();
                }
              }}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              }}
            >
              🔄 New Session
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ 
          background: '#fff', 
          padding: '12px 20px', 
          borderBottom: '1px solid #eee',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
            {steps.map((step, idx) => (
              <React.Fragment key={step.num}>
                <div style={{
                  flex: 1,
                  height: '4px',
                  background: currentStep >= step.num ? '#0e67a2' : '#E0E0E0',
                  borderRadius: '2px',
                  transition: 'background 0.3s'
                }} />
                {idx < steps.length - 1 && <div style={{ width: '2px' }} />}
              </React.Fragment>
            ))}
          </div>
          <div style={{ fontSize: '13px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
            <span>Step {currentStep}/{steps.length}: {steps[currentStep - 1].title}</span>
            <span>{Math.round((currentStep / steps.length) * 100)}%</span>
          </div>
        </div>

        {/* Scrollable Content */}
        <div 
          className="live-studio-v2-scrollable"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: '80px',
            position: 'relative'
          }}
        >
          {/* Error Display */}
          {error && (
            <div style={{
              margin: '16px 20px',
              padding: '12px',
              background: '#fee',
              border: '1px solid #c00',
              borderRadius: '8px',
              color: '#c00',
              fontSize: '14px'
            }}>
              <strong>Error:</strong> {error}
              <button 
                onClick={() => setError(null)}
                style={{
                  float: 'right',
                  background: 'transparent',
                  border: 'none',
                  color: '#c00',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: 0
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Current Step */}
          <div style={{ padding: '20px' }}>
            <CurrentStepComponent
              sessionId={sessionId}
              token={token}
              user={user}
              sessionData={sessionData}
              onComplete={(data) => handleStepComplete(currentStep, data)}
              onBack={handleBack}
              setError={setError}
              setLoading={setLoading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default LiveStudioV2;
